import { Identity } from '../models/auth';
import { isExternal } from './roles';
import { useLocation, useNavigate as useReactRouterNavigate } from 'react-router';
import { useAppSelector } from '../store';
import { selectIdentity } from '../store/entities/identity';
import { LocationStateBack } from '../models/router';

// ============================================================================
// Types
// ============================================================================

type PathParams = Record<string, string | number>;
type QueryParams = Record<string, string | number | boolean>;
type PathResolver = (identity: Identity, params?: PathParams) => string;

// ============================================================================
// Path Configurations
// ============================================================================

const pathConfigs: Record<PathKey, string | PathResolver> = {
  // Simple paths
  accessPointLogs: '/access-point-logs',
  supplier: '/supplier',

  // Role-based paths
  supplierCarNew: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) {
      return '/supplier/cars/new';
    }
    return `/supplier/${params?.supplierId}/cars/new`;
  },
  supplierCars: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return '/supplier/cars';
    return `/supplier/${params?.supplierId}/cars`;
  },
  supplierDetail: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return '/supplier/detail';
    return `/supplier/${params?.supplierId}/detail`;
  },
  supplierEmployees: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return '/supplier/employees';
    return `/supplier/${params?.supplierId}/employees`;
  },
  supplierCarDetail: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return `/supplier/cars/${params?.carId}`;
    return `/supplier/${params?.supplierId}/cars/${params?.carId}`;
  },
  supplierEmployee: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return `/supplier/employee/${params?.employeeId}`;
    return `/supplier/${params?.supplierId}/employee/${params?.employeeId}`;
  },
  supplierEmployeeNew: (identity: Identity, params?: PathParams) => {
    if (isExternal(identity)) return '/supplier/employee/new';
    return `/supplier/${params?.supplierId}/employee/new`;
  },
   supplierRequest: (identity: Identity, params?: PathParams) =>
    `/invitation/${params?.invitationId}/supplier-request/${params?.supplierId ?? 'new'}`,
  userDetail: (identity: Identity, params?: PathParams) => `/user/${params?.userId}`,
  training: '/training',
  trainingRun: (identity: Identity, params?: PathParams) => `/training-run/${params?.versionId}`,
  trainingDetail: (identity: Identity, params?: PathParams) => `/training/${params?.trainingId}`,
  questionnaireQuestions: (identity: Identity, params?: PathParams) =>
    `/questionnaire/${params?.questionnaireId}/${params?.questionnaireVersionId}/${params?.languageCode}`,
  questionnaireVersion: (identity: Identity, params?: PathParams) => `/questionnaire/${params?.questionnaireId}/${params?.questionnaireVersionId}`,
  analytics: '/analytics',
  analyticsDomain: (identity: Identity, params?: PathParams) => `/analytics/${params?.domain}`,
};

// ============================================================================
// Path Keys Enum
// ============================================================================

export enum PathKey {
  AccessPointLogs = 'accessPointLogs',
  Supplier = 'supplier',
  SupplierCarNew = 'supplierCarNew',
  SupplierCars = 'supplierCars',
  SupplierDetail = 'supplierDetail',
  SupplierEmployees = 'supplierEmployees',
  SupplierEmployee = 'supplierEmployee',
  SupplierEmployeeNew = 'supplierEmployeeNew',
  SupplierRequest = 'supplierRequest',
  SupplierCarDetail = 'supplierCarDetail',
  UserDetail = 'userDetail',
  Training = 'training',
  TrainingRun = 'trainingRun',
  TrainingDetail = 'trainingDetail',
  QuestionnaireQuestions = 'questionnaireQuestions',
  QuestionnaireVersion = 'questionnaireVersion',
  Analytics = 'analytics',
  AnalyticsDomain = 'analyticsDomain',
}

// ============================================================================
// Path Builder (internal)
// ============================================================================

const buildPath = (pathKey: PathKey, identity: Identity, params?: PathParams): string => {
  const config = pathConfigs[pathKey];

  if (!config) {
    console.error(`Path configuration not found for key: ${pathKey}`);
    return '/';
  }

  if (typeof config === 'function') {
    return config(identity, params);
  }

  return config;
};

const buildPathWithQuery = (pathKey: PathKey, identity: Identity, params?: PathParams, query?: QueryParams): string => {
  const basePath = buildPath(pathKey, identity, params);

  if (!query || Object.keys(query).length === 0) {
    return basePath;
  }

  const queryString = Object.entries(query)
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
    .join('&');

  return `${basePath}?${queryString}`;
};

// ============================================================================
// Unified Navigation Hook
// ============================================================================

interface NavigateOptions {
  replace?: boolean;
  state?: unknown;
}

/**
 * Unified role-aware navigation hook
 * Provides navigate, getPath, and goBack functionality
 *
 * @example
 * const { navigate, getPath, goBack } = useNavigation();
 *
 * // Navigate to a role-aware path
 * navigate(PathKey.SupplierCarNew, { supplierId: '123' });
 *
 * // With query params
 * navigate(PathKey.SupplierCarNew, { supplierId: '123' }, { backTo: '/supplier/' });
 *
 * // Get path without navigating
 * const path = getPath(PathKey.SupplierCarNew, { supplierId: '123' });
 *
 * // Go back (supports backTo query param, location state, or default)
 * goBack('/default-path');
 */
export const useNavigation = (defaultBackPath?: string | null) => {
  const reactRouterNavigate = useReactRouterNavigate();
  const location = useLocation();
  const identity = useAppSelector(selectIdentity);

  // Navigate function
  const navigate = (pathKey: PathKey | string, params?: PathParams, query?: QueryParams, options?: NavigateOptions) => {
    const isPathKeyEnum = Object.values(PathKey).includes(pathKey as PathKey);

    const path = isPathKeyEnum ? buildPathWithQuery(pathKey as PathKey, identity, params, query) : pathKey;

    reactRouterNavigate(path, options);
  };

  // Get path function
  const getPath = (pathKey: PathKey, params?: PathParams, query?: QueryParams): string => {
    return buildPathWithQuery(pathKey, identity, params, query);
  };

  // Go back function (from useBack.ts)
  const goBack = (defaultPath?: string | null) => {
    const searchParams = new URLSearchParams(location.search);
    const queryBackTo = searchParams.get('backTo');
    const stateBack = (location.state as LocationStateBack)?.back;
    const backTo = queryBackTo || stateBack || defaultPath || defaultBackPath;

    backTo ? reactRouterNavigate(backTo) : window.history.back();
  };

  return {
    navigate,
    getPath,
    goBack,
    currentPath: location.pathname,
    getBackPath: (() => {
      const searchParams = new URLSearchParams(location.search);
      const queryBackTo = searchParams.get('backTo');
      const stateBack = (location.state as LocationStateBack)?.back;
      return queryBackTo || stateBack || defaultBackPath;
    })(),
  };
};
